import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';
import { dateFormatter, request } from '../../../../../utils';
import parse from 'html-react-parser';
import { BASE_URL_FOR_FILE } from '../../../../../utils/BaseUrl';
import { useEffect, useState } from 'react';

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 'bold' }]
});

Font.register({
  family: 'Roboto2',
  fonts: [{ src: '/Roboto-Medium.ttf' }]
});

const styles = StyleSheet.create({
  page: {
    padding: 20
  },
  heading: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 20
  },
  line: {
    height: 0.6,
    backgroundColor: 'black',
    marginVertical: 10,
    marginTop: '40px'
  },
  line2: {
    height: 0.4,
    backgroundColor: 'black',
    marginVertical: 10
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 0,
    marginBottom: 0,
    border: '1px Solid gray',
    padding: '10px',
    borderRadius: '8px'
  },
  contentContainer2: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 10,
    marginBottom: 0
  },
  column: {
    flex: 1,
    paddingHorizontal: 10
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5
  },
  row2: {
    flexDirection: 'row'
  },
  text: {
    fontSize: 9,
    display: 'inline-block',
    textAlign: 'start'
  },
  textBold: {
    fontSize: 9,
    fontWeight: 'bold'
  },
  textUnderlined: {
    textDecoration: 'underline'
  },
  columnContent: {
    width: '25%',
    textAlign: 'center',
    alignItems: 'center',
    paddingLeft: '10%'
  },
  columnHeader: {
    width: '15%',
    backgroundColor: '',
    borderTop: '0.8px solid black',
    borderBottom: '0.8px solid black',
    color: 'black',
    padding: 5,
    textAlign: 'center',
    marginTop: '2%'
  },
  boldText: {
    fontFamily: 'Roboto',
    fontSize: 6,
    fontSize: '10px',
    marginTop: '1%',
    marginLeft: '4%'
  },
  bText: {
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    fontSize: '10px'
  },
  ResultEntryText: {
    fontFamily: 'Roboto',
    fontSize: '12px',
    margin: '0 auto',
    fontWeight: 'ultrabold'
  },
  fontBold: {
    width: '25%',
    paddingLeft: '10%',
    fontFamily: 'Roboto',
    fontWeight: 'ultrabold',
    textDecoration: 'underline'
  },
  parsedContent: {
    marginTop: 10,
    fontSize: 10,
    textAlign: 'justify',
    padding: 10
  },
  imageStyle: {
    width: '10%',
    height: 80 // You can adjust the height or use percentage if needed
  },
  table: { display: 'table', width: '100%', marginTop: 10 },
  tableRow: { display: 'flex', flexDirection: 'row' },
  tableCell: { display: 'flex', width: '50%', padding: 5, border: '0.6px solid black' }
});

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={{ ...style }} {...props}>
    {children}
  </PDFView>
);

const Text = ({ style, content, ...props }) => (
  <PDFText style={{ ...styles.text, ...style }} {...props}>
    {content}
  </PDFText>
);

const COLORS = ['#f0f0f0', '#e0e0e0'];

export const ResultEntryPdf = ({ data }) => {
  const [imagePreview, setImagePreview] = useState([]);

  const parseHtmlToPdfText = (html) => {
    return parse(html, {
      replace: (domNode) => {
        if (domNode.type === 'tag') {
          switch (domNode.name) {
            case 'strong':
              // Render bold text correctly
              return (
                <PDFText style={{ ...styles.boldText, fontFamily: 'Roboto' }}>
                  {domNode.children.map((child) => child.data).join('')}
                </PDFText>
              );
  
            case 'p':
              // Handle <p> tags and render the text within them
              return (
                <PDFText style={styles.text}>
                  {domNode.children
                    .map((child) => {
                      if (child.type === 'text') {
                        return child.data.trim(); // Handle plain text
                      }
                      if (child.type === 'tag' && child.name === 'strong') {
                        return child.children.map((strongChild) => strongChild.data).join(''); // Handle strong tags
                      }
                      return ''; // Handle any other types of nodes
                    })
                    .join(' ')}
                </PDFText>
              );
  
            // Handle header tags (h1, h2, h3, h4, h5, h6)
            case 'h1':
            case 'h2':
            case 'h3':
            case 'h4':
            case 'h5':
            case 'h6':
              return (
                <PDFText style={{ ...styles.headerText, fontFamily: 'Roboto' }}>
                  {domNode.children.map((child) => child.data).join('')}
                </PDFText>
              );
  
            case 'figure':
              // Handle the <figure> tag, which contains the table
              return (
                <PDFView style={styles.figure}>
                  {domNode.children.map((child, childIndex) => {
                    if (child.name === 'table') {
                      // If we encounter a table within the <figure> tag, render it
                      return (
                        <PDFView style={styles.table} key={childIndex}>
                          {child.children.map((tbody, tbodyIndex) => {
                            if (tbody.name === 'tbody') {
                              // Process the rows within <tbody>
                              return (
                                <PDFView style={styles.tableBody} key={tbodyIndex}>
                                  {tbody.children.map((row, rowIndex) => {
                                    if (row.name === 'tr') {
                                      // For each row, render each cell
                                      return (
                                        <PDFView style={styles.tableRow} key={rowIndex}>
                                          {row.children.map((cell, cellIndex) => {
                                            if (cell.name === 'td' || cell.name === 'th') {
                                              // Handle <td> and <th> cells
                                              return (
                                                <PDFView style={styles.tableCell} key={cellIndex}>
                                                  {cell.children.map((child, childIndex) => {
                                                    // Check if the child is a <p> tag inside <td>
                                                    if (child.name === 'p') {
                                                      // Render <p> as a paragraph inside the cell
                                                      return (
                                                        <PDFText style={styles.text} key={childIndex}>
                                                          {child.children.map((pChild) => pChild.data).join('')}
                                                        </PDFText>
                                                      );
                                                    }
                                                    if (child.type === 'text') {
                                                      // Handle plain text
                                                      return (
                                                        <PDFText style={styles.text} key={childIndex}>
                                                          {child.data.trim()}
                                                        </PDFText>
                                                      );
                                                    }
                                                    return null; // Skip non-text elements
                                                  })}
                                                </PDFView>
                                              );
                                            }
                                            return null; // Skip non-cell elements
                                          })}
                                        </PDFView>
                                      );
                                    }
                                    return null; // Skip non-row elements
                                  })}
                                </PDFView>
                              );
                            }
                            return null; // Skip non-body elements
                          })}
                        </PDFView>
                      );
                    }
                    return null; // Skip non-table elements
                  })}
                </PDFView>
              );
  
            case 'ol':
              // Handle the <ol> (ordered list) tag
              return (
                <PDFView style={styles.orderedList}>
                  {domNode.children.map((li, liIndex) => {
                    if (li.name === 'li') {
                      // For each <li> in the <ol>, render a list item with index
                      return (
                        <PDFView style={styles.listItem} key={liIndex}>
                          <PDFText style={styles.listItemText}>
                            {/* Manually add the index */}
                            {`${liIndex + 1}. `} 
                            {li.children.map((child) => child.data).join('')}
                          </PDFText>
                        </PDFView>
                      );
                    }
                    return null; // Skip non-list-item elements
                  })}
                </PDFView>
              );
  
            case 'li':
              // Handle <li> (list item) outside of <ol>
              return (
                <PDFView style={styles.listItem}>
                  <PDFText style={styles.listItemText}>
                    {domNode.children.map((child) => child.data).join('')}
                  </PDFText>
                </PDFView>
              );
  
            default:
              // For any other elements, just return the text
              return <PDFText style={styles.text}>{domNode.data}</PDFText>;
          }
        }
      }
    });
  };
  
  

  const stripHtml = (html) => {
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
  };

  const editorContentPlainText = stripHtml(data?.editorContent || '');

  // Grouping tests by test_name
  const groupedTests = data?.tests?.reduce((acc, test) => {
    const testName = test.test_name; // Group by test_name
    const category = test.category; // Group by category inside each test_name

    if (!acc[testName]) {
      acc[testName] = {}; // Initialize an object to hold categories
    }

    if (!acc[testName][category]) {
      acc[testName][category] = []; // Initialize an array to hold test items for each category
    }

    acc[testName][category].push(test); // Add test item to the appropriate category

    return acc;
  }, {});
  const fetchLabAuthorityDoctor = async () => {
    const response = await request('/lab-authority-doctor-list', { method: 'GET' });
    if (response.success) {
      const datas = response?.data;
      // const imageUrl = new URL(`${BASE_URL_FOR_FILE}/${data[0]?.signature}`).href;
      console.log(data,"IMAGE DATA IS HERE")
      const filteredData = datas.filter((item)=> item._id === data.sign_off_by
      )
      setImagePreview(filteredData);
    } else {
      console.log('Error');
    }
  };

  useEffect(() => {
    fetchLabAuthorityDoctor();
  }, []);

  console.log(imagePreview,"IMAGE PREVIEW")
  return (
    <PDFViewer width="100%" height="100%" showToolbar>
      <Document title={`Result Entry`}>
        <Page size="A4" style={styles.page}>
          <PDFImage src="/logo_Hospital_m.jpeg" style={{ width: '100%', height: '80px', marginBottom: 10 }} />
          <View style={styles.contentContainer}>
            <View style={{ ...styles.column, marginLeft: '1%' }}>
              <View style={styles.row}>
                <Text content="Name               :      " style={styles.bText} />
                <Text style={{ fontSize: '11px' }} content={`${data?.patientName}`} />
              </View>
              <View style={styles.row}>
                <Text content="Age/Gender    :      " style={styles.bText} />
                <Text style={{ fontSize: '11px' }} content={`${data?.age}${data?.gender}`} />
              </View>
              <View style={styles.row}>
                <Text content="Phone No.       :      " style={styles.bText} />
                <Text style={{ fontSize: '11px' }} content={`${data?.mobile_no}`} />
              </View>
              <View style={styles.row}>
                <Text content="Report Date    :      " style={styles.bText} />
                <Text style={{ fontSize: '11px' }} content={`${dateFormatter(data?.createdAt)}`} />
              </View>
            </View>

            <View style={styles.column}>
              <View style={styles.row}>
                <Text content="UHID                       :      " style={styles.bText} />
                <Text style={{ fontSize: '11px' }} content={`${data?.uhid}`} />
              </View>
              <View style={styles.row}>
                <Text content="Report Id                :     " style={styles.bText} />
                <Text style={{ fontSize: '11px' }} content={`${data?.report_id}`} />
              </View>
              <View style={styles.row}>
                <Text content="Collection Date     :     " style={styles.bText} />
                <Text style={{ fontSize: '11px' }} content={`${dateFormatter(data?.sampleCollection?.date)}`} />
              </View>
              <View style={styles.row}>
                <Text content="Doctor Advised     :     " style={styles.bText} />
                <Text style={{ fontSize: '11px' }} content={data?.doctor?.name} />
              </View>
            </View>
          </View>

          {groupedTests &&
            Object.keys(groupedTests).map((testName, idx) => (
              <View key={idx} style={{ width: '100%' }}>
                <Text
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontFamily: 'Roboto',
                    marginTop: '1%'
                  }}
                  content={testName}
                />
                <View style={{ ...styles.row2, width: '100%', flexDirection: 'row' }}>
                  <Text
                    style={{
                      ...styles.columnHeader,
                      width: '50%',
                      fontSize: '12px',
                      fontFamily: 'Roboto',
                      textAlign: 'start'
                    }}
                    content="Investigation"
                  />
                  <Text
                    style={{ ...styles.columnHeader, width: '10%', fontSize: '12px', fontFamily: 'Roboto' }}
                    content="Result"
                  />
                  <Text
                    style={{ ...styles.columnHeader, width: '10%', fontSize: '12px', fontFamily: 'Roboto' }}
                    content=""
                  />
                  <Text
                    style={{ ...styles.columnHeader, width: '20%', fontSize: '12px', fontFamily: 'Roboto' }}
                    content="Reference Value"
                  />
                  <Text
                    style={{ ...styles.columnHeader, width: '20%', fontSize: '12px', fontFamily: 'Roboto' }}
                    content="Unit"
                  />
                </View>
                {Object.keys(groupedTests[testName]).map((category, categoryIdx) => (
                  <View key={categoryIdx} style={{ marginBottom: '5px' }}>
                    <Text
                      style={{
                        fontSize: '11px',
                        fontFamily: 'Roboto',
                        marginTop: '5px',
                        marginBottom: '2px',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        textAlign: 'start'
                      }}
                      content={category} // Display category name
                    />
                    {groupedTests[testName][category].map((test, testIndex) => (
                      <View key={testIndex} style={{ flexDirection: 'row' }}>
                        {/* Test item name with specimen name below it */}
                        <View style={{ width: '48%' }}>
                          <Text
                            content={test.test_item_name || '-'}
                            style={{
                              ...styles.columnText,
                              textAlign: 'start',
                              fontSize: '9px',
                              marginLeft: '10px',
                              fontFamily: 'Roboto2'
                            }}
                          />
                          <Text
                            content={test.specimen || '-'}
                            style={{
                              ...styles.columnText,
                              textAlign: 'start',
                              fontSize: '8px',
                              marginLeft: '10px',
                              fontFamily: 'Roboto2',
                              color: 'black'
                            }}
                          />
                        </View>

                        {/* Test result */}
                        <Text
                          content={test.Result || '-'}
                          style={{
                            ...styles.columnText,
                            width: '10%',
                            textAlign: 'center',
                            fontFamily:
                              test.Result &&
                              test.general_from &&
                              test.general_to &&
                              test.Result >= test.general_from &&
                              test.Result <= test.general_to
                                ? ''
                                : 'Roboto', // Use 'Roboto' when outside the range
                            fontSize: '10px'
                          }}
                        />

                        {/* Status (H, L, or nothing) */}
                        <Text
                          content={
                            test.Result && test.general_from && test.general_to
                              ? parseFloat(test.Result) > parseFloat(test.general_to)
                                ? 'H' // If Result is above the upper range, show 'H'
                                : parseFloat(test.Result) < parseFloat(test.general_from)
                                  ? 'L' // If Result is below the lower range, show 'L'
                                  : '' // If Result is within the range, show nothing
                              : '' // If any required value is missing, show '-'
                          }
                          style={{
                            ...styles.columnText,
                            width: '10%',
                            textAlign: 'center',
                            fontSize: '10px',
                            fontFamily: 'Roboto',
                            color: 'red'
                          }}
                        />

                        {/* General range */}
                        <Text
                          content={`${test.general_from || '-'}- ${test.general_to || '-'}`}
                          style={{
                            ...styles.columnText,
                            width: '20%',
                            textAlign: 'center',
                            fontSize: '10px'
                          }}
                        />

                        {/* Unit name */}
                        <Text
                          content={test.unit_name || '-'}
                          style={{
                            ...styles.columnText,
                            width: '20%',
                            textAlign: 'center',
                            fontSize: '10px'
                          }}
                        />
                      </View>
                    ))}
                  </View>
                ))}
              </View>
            ))}
          {data?.editorContent && <View style={styles.parsedContent}>{parseHtmlToPdfText(data.editorContent)}</View>}
          <View
            style={{
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 20,
              marginTop: '5%',
              borderTop: '1px solid #d3d3d3',
              borderBottom: '1px solid #d3d3d3'
            }}
          >
            {/* Place the "Thanks For Reference" and "End of Report" text here at the top */}
            <View style={{ marginBottom: 10, width: '100%' }}>
              <Text
                style={{ fontSize: 10, textAlign: 'start', color: 'black', fontFamily: 'Roboto2' }}
                content={'Thanks For Reference'}
              />
              <Text
                content={'*******End OF Report********'}
                style={{ fontSize: 9, textAlign: 'center', color: 'black', fontFamily: 'Roboto' }}
              ></Text>
            </View>

            {/* Now, move the doctor details section below */}
            <View
              style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'flex-end', width: '100%' }}
            >
              {imagePreview && imagePreview[0]?.signature && (
                <View style={{ alignItems: 'center' }}>
                  <PDFImage
                    src={`${BASE_URL_FOR_FILE}/${imagePreview[0]?.signature}`}
                    style={{ width: 50, height: 50, marginBottom: 5 }}
                  />
                  <Text
                    style={{ fontSize: 11, textAlign: 'center', color: 'black' }}
                    content={imagePreview[0]?.doctor}
                  />
                         <Text
                    style={{ fontSize: 11, textAlign: 'center', color: 'black' }}
                    content={imagePreview[0]?.heading_1 || ""}
                  />
                     <Text
                    style={{ fontSize: 11, textAlign: 'center', color: 'black' }}
                    content={imagePreview[0]?.heading_2|| ""}
                  />
                     <Text
                    style={{ fontSize: 11, textAlign: 'center', color: 'black' }}
                    content={imagePreview[0]?.heading_3 || ""}
                  />
                </View>
              )}
            </View>
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );
};
